import type { TransferData } from '../../constants'
import { cleanText, normalizeDate, readMeta } from '../../utils/parser'

function getFactCell (label: string): HTMLTableCellElement | null {
  const rows = Array.from(document.querySelectorAll<HTMLTableRowElement>('table.screen-title-details-facts tr'))
  for (const row of rows) {
    const key = cleanText(row.querySelector('th')?.textContent || '')
    if (key.toLowerCase() !== label.toLowerCase()) continue
    return row.querySelector<HTMLTableCellElement>('td')
  }

  return null
}

function getFactText (label: string): string {
  const cell = getFactCell(label)
  if (!cell) return ''
  return cleanText(cell.textContent || '')
}

function getFactLinkedNames (label: string): string {
  const cell = getFactCell(label)
  if (!cell) return ''

  const links = Array.from(cell.querySelectorAll<HTMLAnchorElement>('a.nav-list'))
  if (links.length === 0) return cleanText(cell.textContent || '')

  const names = links
    .map((link) => cleanText((link.childNodes[0]?.textContent || link.textContent || '')))
    .filter(Boolean)

  return names.join(', ')
}

function parseTitle (): string {
  const direct = document.querySelector<HTMLElement>('.screen-title-details-title')?.innerText
  if (direct) return cleanText(direct)
  return readMeta('og:title') || 'book'
}

function parseAuthor (): string {
  const fromFacts = getFactLinkedNames('Author')
  if (fromFacts) return fromFacts

  const attribution = document.querySelector<HTMLElement>('.screen-title-details-attribution')?.innerText
  if (attribution) return cleanText(attribution)

  return ''
}

function parseLanguage (): string {
  return getFactText('Language')
}

function parsePublisher (): string {
  return getFactText('Publisher') || getFactText('Imprint')
}

function parseIsbn (): string {
  return ''
}

function parsePages (): string {
  const pagesFacts = getFactText('Pages')
  const pagesFromFacts = pagesFacts.match(/\b(\d{1,5})\b/)?.[1]
  if (pagesFromFacts) return pagesFromFacts

  const text = document.body?.innerText || ''
  return text.match(/\b(\d{1,5})\s+pages?\b/i)?.[1] || ''
}

function parsePublicationDate (): string {
  const release = getFactText('Release')
  if (release) return normalizeDate(release)
  const published = getFactText('Published')
  if (published) return normalizeDate(published)
  return ''
}

function parseFormat (): string {
  const format = getFactText('Format').toLowerCase()
  if (format.includes('audiobook')) return 'audio'
  if (format.includes('book')) return 'digital'
  return ''
}

function parseDescription (): string {
  const blurbParagraphs = Array.from(document.querySelectorAll<HTMLElement>('div.screen-title-details-blurb p'))
    .map((el) => cleanText(el.textContent || ''))
    .filter(Boolean)
  if (blurbParagraphs.length > 0) return blurbParagraphs.join('\n\n')

  const selectors = [
    '.screen-title-details-blurb',
    '.screen-title-description',
    '.screen-title-details-description',
    '[data-testid="description"]',
    '.book-description'
  ]

  for (const selector of selectors) {
    const el = document.querySelector<HTMLElement>(selector)
    const value = el?.innerText || el?.textContent
    if (value) return cleanText(value)
  }

  return readMeta('description', 'og:description')
}

function parseCoverUrl (): string {
  return (
    document.querySelector<HTMLImageElement>('.screen-title-details-jacket .cover-box-press .cover-box-clip img.directed-image')?.src ||
    readMeta('og:image')
  )
}

export function parseLibbyPage (): TransferData {
  return {
    title: parseTitle(),
    originalTitle: '',
    author: parseAuthor(),
    language: parseLanguage(),
    publisher: parsePublisher(),
    isbn: parseIsbn(),
    pages: parsePages(),
    pubDate: parsePublicationDate(),
    format: parseFormat(),
    desc: parseDescription(),
    rating: '',
    review: '',
    coverUrl: parseCoverUrl()
  }
}
